import { formatEther } from 'viem';
import { weiFromWei } from '../utils/index.js';
import { serializeConditionalOrder } from '../utils/perpsV3.js';
// Define all API endpoints with template literal types
const API_ENDPOINTS = {
    // Static endpoints
    PERPS_V3: {
        ORDERS: '/perpsV3/orders',
        REMOVE_STALE_ORDERS: '/perpsV3/orders/remove-stale',
        MARKETS: '/perpsV3/markets',
    },
    // Functions for dynamic endpoints
    getAccountNoncePath: (accountId) => `/perpsV3/account/${accountId.toString()}/nonce`,
    getMarginSnapshotsPath: (accountId) => `/perpsV3/account/${accountId.toString()}/margin-snapshots`,
};
export default class ApiService {
    sdk;
    baseUrl;
    defaultHeaders = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    };
    constructor(sdk) {
        this.sdk = sdk;
        this.baseUrl = sdk.context.apiUrl;
    }
    /**
     * Create a full URL from an endpoint
     */
    getUrl(endpoint) {
        return new URL(`${this.baseUrl}${endpoint}`);
    }
    /**
     * Generic method to make API requests
     */
    async request(endpoint, method = 'GET', body, params) {
        const url = this.getUrl(endpoint);
        // Add query parameters if provided
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                url.searchParams.append(key, value);
            });
        }
        const options = {
            method,
            headers: this.defaultHeaders,
        };
        if (body) {
            options.body = JSON.stringify(body);
        }
        const response = await fetch(url.toString(), options);
        const data = await response.json();
        if (data.success === false) {
            throw new Error(data.data?.message || 'API request failed');
        }
        return data;
    }
    /**
     * Gets margin snapshots for an account
     */
    async getMarginSnapshots(accountId, chainId, args) {
        const params = {
            networkId: chainId.toString(),
            accountId: accountId.toString(),
            from: args.fromTimestamp.toString(),
            to: args.toTimestamp.toString(),
            resolution: args.resolution.toString(),
        };
        const response = await this.request(API_ENDPOINTS.getMarginSnapshotsPath(accountId), 'GET', undefined, params);
        return response.data.marginSnapshots.map((s) => ({
            ...s,
            margin: weiFromWei(s.margin),
        }));
    }
    /**
     * Gets conditional orders for an account
     */
    async getConditionalOrders(accountId, chainId) {
        const params = {
            networkId: chainId.toString(),
            accountId: accountId.toString(),
        };
        try {
            const response = await this.request(API_ENDPOINTS.PERPS_V3.ORDERS, 'GET', undefined, params);
            return response.data.orders.map((o) => ({
                ...o,
                orderDetails: {
                    ...o.orderDetails,
                    marketId: String(o.orderDetails.marketId),
                    sizeDelta: formatEther(BigInt(o.orderDetails.sizeDelta)),
                    acceptablePrice: formatEther(BigInt(o.orderDetails.acceptablePrice)),
                },
                decodedConditions: {
                    isPriceAbove: o.decodedConditions.isPriceAbove
                        ? formatEther(BigInt(o.decodedConditions.isPriceAbove))
                        : undefined,
                    isPriceBelow: o.decodedConditions.isPriceBelow
                        ? formatEther(BigInt(o.decodedConditions.isPriceBelow))
                        : undefined,
                },
                maxExecutorFee: formatEther(BigInt(o.maxExecutorFee)),
            }));
        }
        catch (e) {
            if (e instanceof Error) {
                throw e;
            }
            throw new Error('Failed to get conditional orders');
        }
    }
    /**
     * Creates conditional orders
     */
    async createConditionalOrders(orders) {
        const body = {
            orders: orders.map((o) => ({
                ...o,
                order: serializeConditionalOrder(o.order),
            })),
        };
        const response = await this.request(API_ENDPOINTS.PERPS_V3.ORDERS, 'POST', body);
        return response.data;
    }
    /**
     * Updates conditional orders
     */
    async updateConditionalOrders(orderUpdates) {
        return this.request(API_ENDPOINTS.PERPS_V3.ORDERS, 'PUT', { orderUpdates });
    }
    /**
     * Cancels conditional orders
     */
    async cancelConditionalOrders(orderUpdates) {
        return this.request(API_ENDPOINTS.PERPS_V3.ORDERS, 'PUT', { orderUpdates });
    }
    /**
     * Removes stale conditional orders
     */
    async removeStaleConditionalOrders(accountId, chainId) {
        const body = {
            accountId: accountId.toString(),
            networkId: chainId,
        };
        await this.request(API_ENDPOINTS.PERPS_V3.REMOVE_STALE_ORDERS, 'PUT', body);
    }
    /**
     * Gets nonce for an account
     */
    async getNonce(accountId) {
        const response = await this.request(API_ENDPOINTS.getAccountNoncePath(accountId));
        return response.data.nonce;
    }
    async getPerpsV3Markets() {
        const response = await this.request(API_ENDPOINTS.PERPS_V3.MARKETS);
        return response.data.markets;
    }
}
//# sourceMappingURL=api.js.map